/**
 * WorkspaceProvider — state backbone dùng chung cho cả 3 workspace.
 *
 * Quản lý:
 *  - Trạng thái sidebar (thu gọn / mở rộng, mobile open)
 *  - Trạng thái AI Panel (mở / đóng) + ngữ cảnh AI theo trang (context-aware)
 *  - Khung chat AI (messages, gửi tin, loading) — tái dùng API `generateStudyAssistant`
 *
 * Mỗi trang workspace gọi `useWorkspace().setAIContext(...)` khi mount để
 * cập nhật gợi ý/hành động nhanh cho AI Panel theo đúng ngữ cảnh trang đó.
 */

import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { generateStudyAssistant, getChildren } from '../../../services/apiService';
import type { Child } from '../../../types';
import type {
  AIPanelContext,
  WorkspaceChatMessage,
  WorkspaceMode,
} from '../types';

interface WorkspaceContextValue {
  mode: WorkspaceMode;

  // Sidebar
  isSidebarCollapsed: boolean;
  toggleSidebarCollapse: () => void;
  isMobileSidebarOpen: boolean;
  setMobileSidebarOpen: (open: boolean) => void;

  // AI Panel
  isAIPanelOpen: boolean;
  openAIPanel: () => void;
  closeAIPanel: () => void;
  toggleAIPanel: () => void;
  aiContext: AIPanelContext;
  setAIContext: (ctx: AIPanelContext) => void;

  // Chat
  messages: WorkspaceChatMessage[];
  isAILoading: boolean;
  sendMessage: (text: string) => Promise<void>;
  resetChat: () => void;

  // Focused Mode (ẩn sidebar + AI panel + header — tập trung soạn giảng)
  focusMode: boolean;
  toggleFocusMode: () => void;

  // Nội dung lấp đầy chiều cao (canvas) thay vì scroll — trang tự bật/tắt.
  fillContent: boolean;
  setFillContent: (v: boolean) => void;

  // ── Child context (parent workspace) ─────────────────────────────────────
  /** Danh sách con của phụ huynh — load từ API 1 lần tại provider. */
  parentChildren: Child[];
  /** Reload danh sách con từ API (sau khi thêm/sửa/xoá). */
  refreshParentChildren: () => void;
  /** ID con đang được xem (chuỗi ID từ edu_children). null khi chưa chọn. */
  activeChildId: string | null;
  setActiveChildId: (id: string | null) => void;

  // ── Class / Subject context (teacher workspace) ──────────────────────────
  /** ID lớp học đang hoạt động (từ API /edu/classes). */
  activeClassId: string | null;
  /** Tên lớp hiển thị (vd "Lớp 10A1"). */
  activeClassName: string;
  /** Cấp học của lớp (vd "THPT"). */
  activeGradeLevel: string;
  /** Môn học đang chọn (vd "Sinh học"). */
  activeSubject: string;
  setActiveClass: (id: string, name: string, gradeLevel?: string) => void;
  setActiveSubject: (subject: string) => void;
}

const WorkspaceContext = createContext<WorkspaceContextValue | null>(null);

export const useWorkspace = (): WorkspaceContextValue => {
  const ctx = useContext(WorkspaceContext);
  if (!ctx) {
    throw new Error('useWorkspace phải được dùng bên trong <WorkspaceProvider>.');
  }
  return ctx;
};

let chatSeq = 0;
const nextId = () => `wmsg_${Date.now()}_${chatSeq++}`;

// ── Chat history persistence ─────────────────────────────────────────────────
const CHAT_HISTORY_PREFIX = 'bookai_ws_chat_';
const MAX_HISTORY_MSGS = 20;

function deriveChatKey(mode: WorkspaceMode, ctx: AIPanelContext): string {
  const raw = ctx.systemContext ?? ctx.greeting ?? mode;
  const normalized = raw.slice(0, 60).replace(/[^a-zA-Z0-9_-]/g, '_');
  return `${CHAT_HISTORY_PREFIX}${mode}_${normalized}`;
}

function loadChatHistory(key: string): WorkspaceChatMessage[] {
  try {
    const raw = localStorage.getItem(key);
    return raw ? (JSON.parse(raw) as WorkspaceChatMessage[]) : [];
  } catch { return []; }
}

function saveChatHistory(key: string, msgs: WorkspaceChatMessage[]): void {
  try {
    const toSave = msgs.filter((m) => !m.pending).slice(-MAX_HISTORY_MSGS);
    if (toSave.length === 0) { localStorage.removeItem(key); return; }
    localStorage.setItem(key, JSON.stringify(toSave));
  } catch { /* localStorage quota */ }
}

interface WorkspaceProviderProps {
  mode: WorkspaceMode;
  /** Tên hiển thị của user, dùng để thay {name} trong lời chào. */
  userName?: string;
  onShowToast?: (message: string, type?: 'success' | 'error' | 'info') => void;
  children: React.ReactNode;
}

export const WorkspaceProvider: React.FC<WorkspaceProviderProps> = ({
  mode,
  userName,
  onShowToast,
  children,
}) => {
  const [isSidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [isMobileSidebarOpen, setMobileSidebarOpen] = useState(false);
  // Trợ lý AI mặc định ĐÓNG — giáo viên tự mở khi cần (tránh chiếm không gian canvas).
  const [isAIPanelOpen, setAIPanelOpen] = useState(false);
  const [focusMode, setFocusMode] = useState(false);
  const [fillContent, setFillContent] = useState(false);

  // Children list — loaded once from API for parent workspace
  const [parentChildren, setParentChildren] = useState<Child[]>([]);
  const refreshParentChildren = useCallback(() => {
    if (mode !== 'parent') return;
    getChildren().then(data => setParentChildren(data.map((c: any) => ({
      id: String(c.id), name: c.name, realName: c.real_name, dob: c.dob,
      gender: c.gender, studentId: c.student_id ?? c.studentId,
      avatarUrl: c.avatar, relationship: c.relationship,
    })))).catch(() => {});
  }, [mode]);

  useEffect(() => { refreshParentChildren(); }, [refreshParentChildren]);

  // Child context — persisted in localStorage (parent workspace)
  const [activeChildId, setActiveChildIdState] = useState<string | null>(
    () => localStorage.getItem('bookai_ws_active_child_id'),
  );
  const setActiveChildId = useCallback((id: string | null) => {
    if (id) localStorage.setItem('bookai_ws_active_child_id', id);
    else localStorage.removeItem('bookai_ws_active_child_id');
    setActiveChildIdState(id);
  }, []);

  // Class / Subject context — persisted in localStorage
  const [activeClassId, setActiveClassId] = useState<string | null>(
    () => localStorage.getItem('bookai_ws_active_class_id'),
  );
  const [activeClassName, setActiveClassName] = useState(
    () => localStorage.getItem('bookai_ws_active_class_name') || '',
  );
  const [activeGradeLevel, setActiveGradeLevel] = useState(
    () => localStorage.getItem('bookai_ws_active_grade_level') || '',
  );
  const [activeSubject, setActiveSubjectState] = useState(
    () => localStorage.getItem('bookai_ws_active_subject') || '',
  );

  const setActiveClass = useCallback((id: string, name: string, gradeLevel = '') => {
    localStorage.setItem('bookai_ws_active_class_id', id);
    localStorage.setItem('bookai_ws_active_class_name', name);
    localStorage.setItem('bookai_ws_active_grade_level', gradeLevel);
    setActiveClassId(id);
    setActiveClassName(name);
    setActiveGradeLevel(gradeLevel);
  }, []);

  const setActiveSubject = useCallback((subject: string) => {
    localStorage.setItem('bookai_ws_active_subject', subject);
    setActiveSubjectState(subject);
  }, []);
  // Ref giữ messages mới nhất để sendMessage không bị stale closure.
  const messagesRef = useRef<WorkspaceChatMessage[]>([]);

  const [aiContext, setAIContextState] = useState<AIPanelContext>(() => {
    const greet: Record<WorkspaceMode, string> = {
      teacher: 'Xin chào{name}! 👋 Tôi là trợ lý soạn giảng. Bạn cần hỗ trợ gì?',
      student: 'Chào{name}! 👋 Mình là Gia sư AI. Hôm nay bạn muốn học gì?',
      parent: 'Chào{name}! 👋 Tôi là trợ lý đồng hành cùng con của bạn.',
    };
    return { greeting: greet[mode] };
  });
  const [messages, setMessages] = useState<WorkspaceChatMessage[]>([]);
  const [isAILoading, setAILoading] = useState(false);

  // Sync ref + persist history whenever messages change.
  React.useEffect(() => {
    messagesRef.current = messages;
    if (chatKeyRef.current) saveChatHistory(chatKeyRef.current, messages);
  }, [messages]);

  // Giữ systemContext mới nhất để đính kèm vào prompt mà không tạo lại sendMessage.
  const systemContextRef = useRef<string | undefined>(undefined);
  // Key localStorage của context hiện tại — dùng khi lưu/tải lịch sử chat.
  const chatKeyRef = useRef<string>('');

  const toggleSidebarCollapse = useCallback(() => setSidebarCollapsed((v) => !v), []);
  const toggleFocusMode = useCallback(() => setFocusMode((v) => !v), []);
  const openAIPanel = useCallback(() => setAIPanelOpen(true), []);
  const closeAIPanel = useCallback(() => setAIPanelOpen(false), []);
  const toggleAIPanel = useCallback(() => setAIPanelOpen((v) => !v), []);

  const setAIContext = useCallback((ctx: AIPanelContext) => {
    // Lưu lịch sử hội thoại context hiện tại trước khi chuyển sang context mới.
    if (chatKeyRef.current) {
      saveChatHistory(chatKeyRef.current, messagesRef.current);
    }
    // Tải lịch sử của context mới (nếu có).
    const newKey = deriveChatKey(mode, ctx);
    chatKeyRef.current = newKey;
    const history = loadChatHistory(newKey);

    systemContextRef.current = ctx.systemContext;
    setAIContextState(ctx);
    setMessages(history);
  }, [mode]);

  const resetChat = useCallback(() => {
    if (chatKeyRef.current) {
      saveChatHistory(chatKeyRef.current, []);
    }
    setMessages([]);
  }, []);

  const sendMessage = useCallback(
    async (text: string) => {
      const trimmed = text.trim();
      if (!trimmed || isAILoading) return;

      const userMsg: WorkspaceChatMessage = { id: nextId(), role: 'user', text: trimmed };
      const pendingMsg: WorkspaceChatMessage = { id: nextId(), role: 'ai', text: '', pending: true };
      setMessages((prev) => [...prev, userMsg, pendingMsg]);
      setAILoading(true);

      try {
        // Lịch sử hội thoại trước đó — dùng ref để tránh stale closure.
        // Ngữ cảnh trang gửi riêng qua `context` (backend đưa vào system prompt).
        const history: { role: 'user' | 'ai'; text: string }[] = [];
        messagesRef.current
          .filter((m) => !m.pending)
          .forEach((m) => history.push({ role: m.role, text: m.text }));
        history.push({ role: 'user', text: trimmed });

        const { reply, soft_notice } = await generateStudyAssistant(history, {
          role: mode,
          context: systemContextRef.current,
        });
        setMessages((prev) =>
          prev.map((m) => (m.id === pendingMsg.id ? { ...m, text: reply, pending: false } : m)),
        );
        if (soft_notice) onShowToast?.(soft_notice, 'info');
      } catch (err: any) {
        const msg = err?.message || 'Không thể kết nối Trợ lý AI. Vui lòng thử lại.';
        setMessages((prev) =>
          prev.map((m) =>
            m.id === pendingMsg.id ? { ...m, text: `⚠️ ${msg}`, pending: false } : m,
          ),
        );
        onShowToast?.(msg, 'error');
      } finally {
        setAILoading(false);
      }
    },
    [isAILoading, mode, onShowToast],
  );

  const value = useMemo<WorkspaceContextValue>(
    () => ({
      mode,
      isSidebarCollapsed,
      toggleSidebarCollapse,
      isMobileSidebarOpen,
      setMobileSidebarOpen,
      isAIPanelOpen,
      openAIPanel,
      closeAIPanel,
      toggleAIPanel,
      aiContext,
      setAIContext,
      messages,
      isAILoading,
      sendMessage,
      resetChat,
      focusMode,
      toggleFocusMode,
      fillContent,
      setFillContent,
      parentChildren,
      refreshParentChildren,
      activeChildId,
      setActiveChildId,
      activeClassId,
      activeClassName,
      activeGradeLevel,
      activeSubject,
      setActiveClass,
      setActiveSubject,
    }),
    [
      mode,
      isSidebarCollapsed,
      toggleSidebarCollapse,
      isMobileSidebarOpen,
      isAIPanelOpen,
      openAIPanel,
      closeAIPanel,
      toggleAIPanel,
      aiContext,
      setAIContext,
      messages,
      isAILoading,
      sendMessage,
      resetChat,
      focusMode,
      toggleFocusMode,
      fillContent,
      parentChildren,
      refreshParentChildren,
      activeChildId,
      setActiveChildId,
      activeClassId,
      activeClassName,
      activeGradeLevel,
      activeSubject,
      setActiveClass,
      setActiveSubject,
    ],
  );

  // userName được expose qua context phụ trợ nếu cần (lời chào).
  ;(value as any).userName = userName;

  return <WorkspaceContext.Provider value={value}>{children}</WorkspaceContext.Provider>;
};
